import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import productBadgeMessages from 'i18n/productBadgeMessages'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'

interface Props {
  fromPrice: number
  duration: number
  priceBlurbDuration?: number
  withFlights?: boolean
  roomCount: number
  special?: boolean
  specialName?: string
  displayMemberPrice: boolean
  hasLoyaltyRoomUpgrade?: boolean
  saleUnit?: string
}

function OfferCalendarSidebarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    priceBlurbDuration,
    withFlights,
    roomCount,
    special,
    specialName,
    displayMemberPrice,
    hasLoyaltyRoomUpgrade,
    saleUnit,
  } = props

  const intl = useIntl()
  const { formatSaleUnitLabel, getSaleUnitPluralNoun } = useSaleUnitFormatting()
  const defaultSpecialName = intl.formatMessage(productBadgeMessages.limitedTimeSpecial)
  const resolvedSpecialName = specialName ?? defaultSpecialName
  const nights = priceBlurbDuration ?? duration
  const averagePrice = Math.ceil(fromPrice / (roomCount > 1 ? roomCount : nights))

  const displayUnit = saleUnit ?? 'room'

  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const priceElement = <b><FormatCurrency value={fromPrice} /></b>
  const priceWithAsterisk = <BodyText variant="small" weight="bold" colour="highlight-primary-dark" as="span"><FormattedMessage
    id="j4Pr3CJCMiA3fwXE2ASW"
    defaultMessage="{price}*"
    
    values={{ price: <FormatCurrency value={fromPrice} /> }}
  />
  </BodyText>
  const averagePriceElement = <FormatCurrency value={averagePrice} />

  let priceBlurb: React.ReactNode
  if (roomCount > 1) {
    priceBlurb = <FormattedMessage
      id="oSXU6s6fXcupSOmrqn9_"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} from {price}"
      
      values={{
        roomCount,
        saleUnitLabel: getSaleUnitPluralNoun(displayUnit),
        nights,
        price: priceElement,
      }}
    />
  } else {
    priceBlurb = <FormattedMessage
      id="TCqHRCs9MZQoxlqMaeou"
      defaultMessage="{nights, plural, one {# night} other {# nights}} from {price}"
      
      values={{
        nights,
        price: priceElement,
      }}
    />
  }

  let priceBlurbWithFlights: React.ReactNode
  if (roomCount > 1) {
    priceBlurbWithFlights = <FormattedMessage
      id="QvhSqr21F_DijVdQqblr"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      
      values={{
        roomCount,
        saleUnitLabel: getSaleUnitPluralNoun(displayUnit),
        nights,
        price: priceWithAsterisk,
      }}
    />
  } else {
    priceBlurbWithFlights = <FormattedMessage
      id="twejUvQQYAlyAehxLIuW"
      defaultMessage="{nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      
      values={{
        nights,
        price: priceWithAsterisk,
      }}
    />
  }

  let averagePriceBlurb: React.ReactNode
  if (roomCount > 1 && saleUnit) {
    averagePriceBlurb = <FormattedMessage
      id="7s-BiTNCirv-oqSf3yp7"
      defaultMessage="Average {price}/{saleUnit}"
      
      values={{
        price: averagePriceElement,
        saleUnit: formatSaleUnitLabel(saleUnit, { withLeadingSlash: false }),
      }}
    />
  } else if (roomCount > 1) {
    averagePriceBlurb = <FormattedMessage
      id="AIZmRyOOzvRUMapw_Gjw"
      defaultMessage="Average {price}/room"
      
      values={{ price: averagePriceElement }}
    />
  } else {
    averagePriceBlurb = <FormattedMessage
      id="ze4LxHvGjcB5JBrGJzd7"
      defaultMessage="Average {price}/night"
      
      values={{ price: averagePriceElement }}
    />
  }

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
    >
      {!withFlights && <BodyText variant="small" colour="normal-contrast">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'Ozc8LMUkZZJFt5eCJKyw',
                defaultMessage: 'Showing {programName} member prices',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurb}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="light-foreground-alt" as="span" style={{ marginInlineStart: '0.25em' }}>
          {averagePriceBlurb}
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="normal-contrast">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'R9ragplcwTdqytggkDI-',
                defaultMessage: 'Showing {programName} member prices',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurbWithFlights}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="light-foreground-alt" as="span" style={{ marginInlineStart: '0.25em' }}>
          {averagePriceBlurb}
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {special && <CaptionTextBlock
          variant="medium"
          colour="light-foreground-alt"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {resolvedSpecialName}
        </CaptionTextBlock>}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="light-foreground-alt"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          {intl.formatMessage({
            id: 'Q6K89Xi9Vf_b6sIcUpHx',
            defaultMessage: 'Free upgrade available',
          })}
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    displayMemberPrice: state.displayMemberPrice,
    saleUnit: state.offer?.saleUnit,
  }
}, OfferCalendarSidebarPriceBlurb)
